<template>
  <div class="kanban-day-picker" data-tour="kb-dia">
    <div class="kanban-day-picker__group">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            tile
            class="kanban-day-picker__arrow"
            :loading="loadingDirection === 'previous'"
            :disabled="!!loadingDirection"
            v-bind="attrs"
            v-on="on"
            aria-label="Dia anterior com consultas"
            @click="goToNeighbor('previous')"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
        </template>
        <span>Dia anterior com consultas</span>
      </v-tooltip>

      <v-menu
        v-model="menu"
        :close-on-content-click="false"
        offset-y
        left
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            text
            tile
            class="kanban-day-picker__date"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon small class="mr-2">mdi-calendar-month-outline</v-icon>
            {{ label }}
          </v-btn>
        </template>

        <v-date-picker
          :value="value"
          :max="today"
          :allowed-dates="isAllowed"
          :picker-date.sync="pickerMonth"
          color="primary"
          locale="pt-br"
          first-day-of-week="0"
          no-title
          @input="pick"
        />
      </v-menu>

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            tile
            class="kanban-day-picker__arrow"
            :loading="loadingDirection === 'next'"
            :disabled="isToday || !!loadingDirection"
            v-bind="attrs"
            v-on="on"
            aria-label="Próximo dia com consultas"
            @click="goToNeighbor('next')"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </template>
        <span>Próximo dia com consultas</span>
      </v-tooltip>
    </div>

    <v-btn
      v-if="!isToday"
      outlined
      color="primary"
      class="kanban-day-picker__today"
      @click="$emit('input', today)"
    >
      <v-icon small class="mr-1">mdi-calendar-today</v-icon>
      Hoje
    </v-btn>
  </div>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import {
  fetchDaysWithConsultations,
  fetchNeighborDay,
} from "@/services/kanbanHistoryService";

/**
 * Histórico do kanban — escolher o dia do quadro.
 *
 * O calendário só deixa clicar nos dias que tiveram consulta (e em hoje, que é
 * onde o kanban abre). As setas pulam para o dia com consulta mais próximo,
 * atravessando meses; para a frente, param em hoje. Sem dias futuros.
 *
 * Os dias de cada mês vêm do backend uma vez e ficam guardados enquanto o
 * componente vive: navegar pelo calendário não repete a mesma busca.
 */
export default {
  name: "KanbanDayPicker",

  props: {
    // "YYYY-MM-DD"
    value: { type: String, required: true },
    // "YYYY-MM-DD" no fuso de quem olha
    today: { type: String, required: true },
  },

  setup() {
    const { info, erroDeRequisicao } = useSnackbar();
    return { info, erroDeRequisicao };
  },

  data() {
    return {
      menu: false,
      pickerMonth: this.value.slice(0, 7),
      // "YYYY-MM" => ["YYYY-MM-DD", ...]
      daysByMonth: {},
      loadingDirection: null,
    };
  },

  computed: {
    isToday() {
      return this.value === this.today;
    },

    label() {
      // Meio-dia: sem hora, `new Date("2026-10-15")` é meia-noite UTC — no
      // Brasil, o dia anterior.
      const date = new Date(`${this.value}T12:00:00`);
      const text = date.toLocaleDateString("pt-BR", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      return this.isToday ? `Hoje, ${text}` : text;
    },
  },

  watch: {
    pickerMonth: {
      immediate: true,
      handler(month) {
        if (month) this.loadMonth(month);
      },
    },

    value(date) {
      this.pickerMonth = date.slice(0, 7);
    },
  },

  methods: {
    isAllowed(date) {
      if (date === this.today) return true;
      return (this.daysByMonth[date.slice(0, 7)] || []).includes(date);
    },

    async loadMonth(month) {
      if (this.daysByMonth[month]) return;
      try {
        const days = await fetchDaysWithConsultations(month);
        this.$set(this.daysByMonth, month, days);
      } catch (e) {
        this.erroDeRequisicao(e, "Erro ao carregar os dias com consultas");
      }
    },

    pick(date) {
      this.menu = false;
      this.$emit("input", date);
    },

    async goToNeighbor(direction) {
      this.loadingDirection = direction;
      try {
        const date = await fetchNeighborDay(this.value, direction);

        if (date) {
          this.$emit("input", date);
        } else if (direction === "next") {
          // Depois do último dia com consulta só resta hoje.
          this.$emit("input", this.today);
        } else {
          this.info("Não há consultas antes deste dia.");
        }
      } catch (e) {
        this.erroDeRequisicao(e, "Erro ao buscar o dia com consultas");
      } finally {
        this.loadingDirection = null;
      }
    },
  },
};
</script>

<style scoped>
/* Um controle só, [‹ | data | ›], com as três partes na mesma altura, e o
   "Hoje" ao lado com a mesma altura. */
.kanban-day-picker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.kanban-day-picker__group {
  display: inline-flex;
  align-items: stretch;
  height: 40px;
  border: 1px solid var(--ds-blue-100);
  border-radius: 10px;
  background: var(--ds-surface-white);
  overflow: hidden;
}

.kanban-day-picker__group .v-btn {
  height: 38px !important;
  margin: 0;
  border-radius: 0 !important;
}

.kanban-day-picker__arrow {
  width: 40px !important;
  min-width: 40px !important;
}

.kanban-day-picker__date {
  min-width: 232px !important;
  padding: 0 14px !important;
  border-left: 1px solid var(--ds-blue-100);
  border-right: 1px solid var(--ds-blue-100);
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  white-space: nowrap;
}

/* Sem o cinza de foco que ficava no botão depois de fechar o calendário. */
.kanban-day-picker__date:focus::before {
  opacity: 0 !important;
}

.kanban-day-picker__today {
  height: 40px !important;
  border-radius: 10px !important;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}
</style>
